import { ReservationsDataOutDTO } from '@api/ringil-api.v2';
import { OptionType } from '@components/RingilFilters';
import { CalendarFilter } from './useCalendarFilter';

/**
 * "Highlight" filters — unlike locations/ramps these never reach the API (the reservations endpoint
 * takes only locations, ramps and a day). They are applied to the events already on screen: matching
 * reservations keep their normal look and gain a border, the rest are greyed out. Nothing disappears,
 * so the grid still shows how busy a ramp really is.
 */
export type HighlightState = 'match' | 'dim' | null;

const asArray = (value: unknown): string[] => (Array.isArray(value) ? (value as string[]) : []);

/** Distinct, sorted options for a highlight chip, taken from the reservations currently loaded. */
const toOptions = (values: { value: string; label: string }[]): OptionType[] => {
  const byValue = new Map<string, string>();
  values.forEach(v => {
    if (v.value) byValue.set(v.value, v.label || v.value);
  });
  return [...byValue.entries()]
    .map(([value, label]) => ({ value, label, active: true }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

export interface HighlightOptions {
  applicationIds: OptionType[];
  carPlates: OptionType[];
  carriers: OptionType[];
  expeditionApplicationIds: OptionType[];
  suppliers: OptionType[];
}

export const buildHighlightOptions = (events: ReservationsDataOutDTO[]): HighlightOptions => ({
    applicationIds: toOptions(events.map(e => ({ value: e.reservationApplicationId, label: e.reservationApplicationId }))),
    carPlates: toOptions(events.map(e => ({ value: e.carPlate, label: e.carPlate }))),
    // One "Carrier / Partner" list: a reservation is either carried by someone or made on behalf of a
    // partner company, and the user shouldn't have to know which of the two a given name came from.
    carriers: toOptions([
      ...events.map(e => ({ value: e.carrierId, label: e.carrierName })),
      ...events.map(e => ({ value: e.onBehalfOf?.id, label: e.onBehalfOf?.name ?? e.onBehalfOf?.externalId })),
    ]),
    // The linked expedition is identified by its application id, the human-readable one.
    expeditionApplicationIds: toOptions(events.map(e => ({ value: e.linkedToExpCoords?.applicationId, label: e.linkedToExpCoords?.applicationId }))),
    // Supplier of the notification the linked expedition came from. The API stores supplier *ids*,
    // so that is the chip's value; the company name is only the label.
    suppliers: toOptions(
      events.map(e => ({ value: e.linkedSupplierNotification?.supplierCompanyId, label: e.linkedSupplierNotification?.supplierCompanyName })),
    ),
});

/** Whether any highlight filter holds a value — nothing is greyed out until one does. */
export const isHighlightActive = (filter: CalendarFilter | undefined): boolean =>
  [filter?.applicationIds, filter?.carPlates, filter?.carrierIds, filter?.expeditionApplicationIds, filter?.supplierIds].some(
    v => asArray(v).length > 0,
  );

/** A reservation matches when it satisfies every active filter (values within one filter are OR-ed). */
export const matchesHighlight = (reservation: ReservationsDataOutDTO, filter: CalendarFilter | undefined): boolean => {
  const applicationIds = asArray(filter?.applicationIds);
  const carPlates = asArray(filter?.carPlates);
  const carrierIds = asArray(filter?.carrierIds);
  const expeditionApplicationIds = asArray(filter?.expeditionApplicationIds);
  const supplierIds = asArray(filter?.supplierIds);

  if (applicationIds.length > 0 && !applicationIds.includes(reservation.reservationApplicationId)) return false;
  if (carPlates.length > 0 && !carPlates.includes(reservation.carPlate)) return false;
  if (carrierIds.length > 0 && !carrierIds.includes(reservation.carrierId) && !carrierIds.includes(reservation.onBehalfOf?.id)) return false;
  if (expeditionApplicationIds.length > 0 && !expeditionApplicationIds.includes(reservation.linkedToExpCoords?.applicationId)) return false;
  if (supplierIds.length > 0 && !supplierIds.includes(reservation.linkedSupplierNotification?.supplierCompanyId)) return false;
  return true;
};

export const highlightStateOf = (reservation: ReservationsDataOutDTO, filter: CalendarFilter | undefined): HighlightState => {
  if (!isHighlightActive(filter)) return null;
  return matchesHighlight(reservation, filter) ? 'match' : 'dim';
};